/**
 * <hero-cinematic> — hero da home com parallax de profundidade por CSS vars
 * e o painel de vídeo do split (F9/T2).
 *
 * Sem WebGL local: o markup real usa data-webgl="off" (canvas WebGL global é
 * a atmosfera de fundo em assets/webgl-hover.js, não este componente). Só
 * roda em ponteiro fino sem prefers-reduced-motion — mobile e reduced motion
 * ficam com --par-x/--par-y em 0, que é o valor inicial usado pelo CSS.
 */

class HeroCinematic extends HTMLElement {
  connectedCallback() {
    // Parallax de profundidade por CSS vars (wordmark / produto): roda só
    // com ponteiro fino e sem reduced-motion — a media query do CSS já
    // corta as demais animações, e sem listener as vars ficam 0 (repouso).
    if (matchMedia('(hover: hover) and (prefers-reduced-motion: no-preference)').matches) {
      this.aoMoverPonteiroCss = (evento) => {
        const x = (evento.clientX / innerWidth - 0.5) * 2;
        const y = (evento.clientY / innerHeight - 0.5) * 2;
        this.style.setProperty('--par-x', x.toFixed(3));
        this.style.setProperty('--par-y', y.toFixed(3));
      };
      addEventListener('pointermove', this.aoMoverPonteiroCss, { passive: true });
    }

    // Painel de vídeo (F9/T2): o <video> do markup sempre nasce com
    // autoplay/loop (progressive enhancement — sem JS, o vídeo ainda roda
    // via atributos HTML nativos). Sob prefers-reduced-motion, este
    // componente pausa e devolve o poster; o CSS não alcança <video> (não
    // dá pra "desligar" autoplay só com media query), por isso o corte é
    // aqui. matchMedia (não only-once) porque o SO pode mudar a preferência
    // com a aba aberta (macOS/Windows expõem o toggle sem recarregar).
    //
    // Botão pausar/retomar (achado 2/review, WCAG 2.2.2): o <video> é
    // decorativo (aria-hidden no markup) e só existe quando há vídeo
    // selecionado — o botão nasce junto, no mesmo ramo do liquid.
    this.video = this.querySelector('.hero-cinematic__painel video');
    this.botaoPausa = this.querySelector('[data-hero-pausa]');
    if (this.video) {
      this.consultaMotion = matchMedia('(prefers-reduced-motion: reduce)');
      // Precedência: uma vez que o usuário interage manualmente com o
      // botão, a escolha dele vale pro resto da vida da página — o
      // listener de mudança do SO (aoMudarMotion) para de sincronizar o
      // vídeo, senão a preferência do sistema "desfaria" silenciosamente
      // um play/pause que o usuário acabou de pedir.
      this.interacaoManual = false;
      this.aoMudarMotion = () => {
        if (!this.interacaoManual) this.sincronizarVideo();
      };
      this.consultaMotion.addEventListener('change', this.aoMudarMotion);
      this.sincronizarVideo();

      if (this.botaoPausa) {
        this.aoClicarPausa = () => {
          this.interacaoManual = true;
          if (this.video.paused) {
            this.video.setAttribute('autoplay', '');
            this.video.play()?.catch(() => {
              // Autoplay/play programático bloqueado: o poster cobre o
              // estado, mas o rótulo do botão não pode mentir (achado 3/T8)
              // — sem isto, um play() rejeitado deixava aria-pressed/label
              // dizendo "pausado" com o vídeo na real parado no poster.
              this.atualizarBotaoPausa();
            });
          } else {
            this.video.pause();
          }
          this.atualizarBotaoPausa();
        };
        this.botaoPausa.addEventListener('click', this.aoClicarPausa);
      }
    }
  }

  sincronizarVideo() {
    if (this.consultaMotion.matches) {
      // .load() reseta o elemento pro estado "não iniciado", que é o único
      // jeito confiável entre browsers de voltar a exibir o poster depois
      // que o playback já começou (.pause() sozinho mantém o frame atual
      // congelado, não o poster).
      this.video.pause();
      this.video.removeAttribute('autoplay');
      this.video.load();
    } else if (this.video.paused) {
      this.video.setAttribute('autoplay', '');
      this.video.play()?.catch(() => {
        // Autoplay bloqueado pelo browser (raro com muted, mas possível em
        // alguns modos de economia de energia): o poster cobre o estado —
        // nenhuma ação extra necessária.
      });
    }
    this.atualizarBotaoPausa();
  }

  atualizarBotaoPausa() {
    if (!this.botaoPausa) return;
    const pausado = this.video.paused;
    this.botaoPausa.setAttribute('aria-pressed', String(pausado));
    this.botaoPausa.setAttribute('aria-label', pausado ? 'Reproduzir vídeo' : 'Pausar vídeo');
  }

  disconnectedCallback() {
    removeEventListener('pointermove', this.aoMoverPonteiroCss);
    this.consultaMotion?.removeEventListener('change', this.aoMudarMotion);
    this.botaoPausa?.removeEventListener('click', this.aoClicarPausa);
  }
}

if (!customElements.get('hero-cinematic')) {
  customElements.define('hero-cinematic', HeroCinematic);
}
